import React from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import RoundedLabel from "../../common/label/RoundedLabel/RoundedLabel";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { ArrowLeft, Gear } from "@phosphor-icons/react";

type CourseHeaderProps = {
  title: string;
  label: string;
  isDraft: boolean;
  moduleCount: number;
  onExit: () => void;
  onOpenSettings: () => void;
  onNavigateToStudentView: () => void;
  onOpenGenerateDialog: () => void;
  onTriggerPublishToggle: () => void;
  isSubmitting: boolean;
  showRightSection: boolean;
  isEditor?: boolean;
};

const HEADER_HEIGHT = 48;
const BUTTON_HEIGHT = 36;

const CourseHeader = ({
  title,
  label,
  isDraft = true,
  moduleCount = 0,
  onExit,
  onOpenSettings,
  onNavigateToStudentView,
  onOpenGenerateDialog,
  onTriggerPublishToggle,
  isSubmitting = false,
  showRightSection = true,
  isEditor = false,
}: CourseHeaderProps) => {
  return (
    <View style={styles.header} accessibilityRole="header">
      <View style={styles.headerLeft}>
        <View style={styles.exitButtonContainer}>
          <CustomButton
            leftIcon={<ArrowLeft size={24} color="#667085" weight="bold" />}
            text={isEditor ? "Exit Editor" : "Exit"}
            onPress={onExit}
            size="s"
            styleType="transparent"
            style={styles.exitButton}
            textStyle={styles.exitButtonText}
          />
        </View>
        <CustomSpacing type="horizontal" size="xl" />
        <CustomText
          text={title}
          useTranslationText={false}
          weight="ultraBold"
          style={{ color: "#101828" }}
          aria-level="1"
          role="heading"
        />
        <CustomSpacing type="horizontal" size="s" />
        {label && (
          <RoundedLabel
            text={label}
            textStyle={["Published", "Graded"].includes(label) ? styles.publishedText : styles.draftText}
            style={["Published", "Graded"].includes(label) ? styles.publishedLabel : styles.draftLabel}
            padding={[12, 4]}
            isRounded={true}
          />
        )}
      </View>

      {showRightSection && (
        <View style={styles.headerRight}>
          {moduleCount === 0 && (
            <>
              <CustomButton
                size="s"
                text="Generate Course"
                styleType="primaryTransparentStroke"
                textStyle={{ fontSize: 14, fontWeight: 700 }}
                onPress={onOpenGenerateDialog}
                disabled={isSubmitting}
                style={
                  isSubmitting ? [{ backgroundColor: "#ccc" }, styles.generalButtonStyle] : styles.generalButtonStyle
                }
              />
              <CustomSpacing type="horizontal" size="s" />
            </>
          )}
          <CustomButton
            size="s"
            text="Student View"
            styleType="primaryTransparentStroke"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            onPress={onNavigateToStudentView}
            style={styles.generalButtonStyle}
          />
          <CustomSpacing type="horizontal" size="s" />
          <CustomButton
            size="s"
            text={isDraft ? "Publish" : "Unpublish"}
            styleType="primary"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            onPress={onTriggerPublishToggle}
            disabled={isSubmitting}
            style={isSubmitting ? [{ backgroundColor: "#ccc" }, styles.primaryButtonStyle] : styles.primaryButtonStyle}
          />
          <CustomSpacing type="horizontal" size="s" />
          <CustomButton
            size="s"
            text=""
            leftIcon={<Gear size={24} color="#3E68FE" />}
            styleType="primaryTransparentStroke"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            onPress={onOpenSettings}
            disabled={isSubmitting}
            style={isSubmitting ? [{ backgroundColor: "#ccc" }, styles.generalButtonStyle] : styles.generalButtonStyle}
          />
        </View>
      )}
    </View>
  );
};

export default CourseHeader;

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: "#EAECF0",
    borderBottomWidth: 1,
    height: HEADER_HEIGHT,
  },
  headerLeft: {
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  headerRight: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    paddingRight: 20,
  },
  exitButtonContainer: {
    borderRightColor: "#EAECF0",
    borderRightWidth: 1,
    height: HEADER_HEIGHT,
  },
  exitButton: {
    height: HEADER_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
    padding: 0,
  },
  exitButtonText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: 700,
  },
  draftLabel: {
    backgroundColor: "#EAECF0",
    borderRadius: 360,
  },
  publishedLabel: {
    backgroundColor: "#66C61C",
    borderRadius: 360,
  },
  publishedText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 700,
  },
  draftText: {
    color: "#667085",
    fontSize: 12,
    fontWeight: 700,
  },
  generalButtonStyle: {
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: BUTTON_HEIGHT,
    borderColor: "#EAECF0",
  },
  primaryButtonStyle: {
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: BUTTON_HEIGHT,
  },
});
